import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek } from 'date-fns';
import type { DayTheme } from '@/types';
import { useWeeklyPlan, getWeekId } from '@/hooks/useWeeklyPlan';
import { useWeeklyMetrics } from '@/hooks/useDeepWorkMetrics';
import { WeeklyHeader, WeeklyGoalsSection, DayThemesGrid, NotesSection } from '@/components/weekly';
import styles from './WeeklyPlanningPage.module.css';

export function WeeklyPlanningPage() {
  const navigate = useNavigate();

  // Week navigation state
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  const weekEnd = useMemo(
    () => endOfWeek(currentWeekStart, { weekStartsOn: 1 }),
    [currentWeekStart]
  );
  const isCurrentWeek = isSameWeek(currentWeekStart, new Date(), { weekStartsOn: 1 });

  // Get the week ID for the current week
  const weekId = useMemo(() => getWeekId(currentWeekStart), [currentWeekStart]);

  // Use the weekly plan hook
  const {
    weeklyPlan,
    isLoading: isPlanLoading,
    getOrCreateWeeklyPlan,
    addGoal,
    toggleGoalCompletion,
    removeGoal,
    updateGoals,
    setDayTheme,
    updateNotes,
  } = useWeeklyPlan({ weekId });

  // Use weekly metrics hook
  const { metrics: weeklyMetrics } = useWeeklyMetrics(weekId);

  // Initialize the weekly plan when the week changes
  useEffect(() => {
    if (!weeklyPlan && !isPlanLoading) {
      getOrCreateWeeklyPlan();
    }
  }, [weeklyPlan, isPlanLoading, getOrCreateWeeklyPlan]);

  // Extract data from the plan with fallbacks
  const goals = weeklyPlan?.goals ?? [];
  const dayThemes = weeklyPlan?.dayThemes ?? {};
  const notes = weeklyPlan?.notes ?? '';

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handlePrevWeek = useCallback(() => {
    setCurrentWeekStart((prev) => subWeeks(prev, 1));
  }, []);

  const handleNextWeek = useCallback(() => {
    setCurrentWeekStart((prev) => addWeeks(prev, 1));
  }, []);

  const handleToday = useCallback(() => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  }, []);

  // Goal handlers
  const handleAddGoal = useCallback(
    async (title: string) => {
      await addGoal(title);
    },
    [addGoal]
  );

  const handleToggleGoal = useCallback(
    async (id: string) => {
      await toggleGoalCompletion(id);
    },
    [toggleGoalCompletion]
  );

  const handleRemoveGoal = useCallback(
    async (id: string) => {
      await removeGoal(id);
    },
    [removeGoal]
  );

  const handleEditGoal = useCallback(
    async (id: string, title: string) => {
      const updatedGoals = goals.map((g) => (g.id === id ? { ...g, title } : g));
      await updateGoals(updatedGoals);
    },
    [goals, updateGoals]
  );

  // Theme handler
  const handleThemeChange = useCallback(
    async (dateKey: string, theme: DayTheme) => {
      await setDayTheme(dateKey, theme);
    },
    [setDayTheme]
  );

  // Notes handler
  const handleNotesChange = useCallback(
    async (newNotes: string) => {
      await updateNotes(newNotes);
    },
    [updateNotes]
  );

  return (
    <div className={styles.container}>
      <WeeklyHeader
        weekStart={currentWeekStart}
        weekEnd={weekEnd}
        onBack={handleBack}
        onPrevWeek={handlePrevWeek}
        onNextWeek={handleNextWeek}
        onToday={handleToday}
        isCurrentWeek={isCurrentWeek}
        totalDeepWorkMinutes={weeklyMetrics?.totalDeepWorkMinutes ?? 0}
      />

      <main className={styles.main}>
        <motion.div
          className={styles.content}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <WeeklyGoalsSection
            goals={goals}
            onAddGoal={handleAddGoal}
            onToggleGoal={handleToggleGoal}
            onRemoveGoal={handleRemoveGoal}
            onEditGoal={handleEditGoal}
          />

          <DayThemesGrid
            weekStart={currentWeekStart}
            weekEnd={weekEnd}
            dayThemes={dayThemes}
            onThemeChange={handleThemeChange}
          />

          <NotesSection notes={notes} onChange={handleNotesChange} />
        </motion.div>
      </main>
    </div>
  );
}

export default WeeklyPlanningPage;
